import React, { useState } from 'react';
import {
  FileText,
  Calculator,
  CheckSquare,
  Download,
  Lock,
  ArrowRight,
  Bookmark
} from 'lucide-react';

interface Resource {
  id: string;
  title: string;
  type: 'template' | 'spreadsheet' | 'checklist';
  isLocked: boolean;
  category: 'planning' | 'marketing' | 'finance';
}

interface ResourceLibraryProps {
  isAuthenticated: boolean;
  onLoginClick: () => void;
  bookmarkedResources?: string[];
  onToggleBookmark?: (resourceId: string, title: string) => void;
}

const RESOURCES: Resource[] = [
  { id: 'business-plan', title: 'Business Plan Template', type: 'template', isLocked: false, category: 'planning' },
  { id: 'profit-calculator', title: 'Profit Calculator', type: 'spreadsheet', isLocked: false, category: 'finance' },
  { id: 'launch-checklist', title: 'Launch Checklist', type: 'checklist', isLocked: false, category: 'planning' },
  { id: 'brand-guide', title: 'Brand Style Guide', type: 'template', isLocked: true, category: 'marketing' },
  { id: 'social-calendar', title: 'Content Calendar', type: 'spreadsheet', isLocked: true, category: 'marketing' },
  { id: 'email-templates', title: 'Email Templates', type: 'template', isLocked: true, category: 'marketing' },
];

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'planning', label: 'Planning' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'finance', label: 'Finance' },
];

const TYPE_ICONS = {
  template: FileText,
  spreadsheet: Calculator,
  checklist: CheckSquare,
};

const ResourceLibrary: React.FC<ResourceLibraryProps> = ({
  isAuthenticated,
  onLoginClick,
  bookmarkedResources = [],
  onToggleBookmark
}) => {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredResources = activeCategory === 'all'
    ? RESOURCES
    : RESOURCES.filter(r => r.category === activeCategory);

  const handleDownload = (resource: Resource) => {
    if (resource.isLocked && !isAuthenticated) {
      onLoginClick();
      return;
    }
    console.log('Downloading:', resource.id);
  };

  return (
    <div className="min-h-full bg-neutral-50 px-5 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-neutral-900">Tools</h1>
        <span className="text-sm text-neutral-400">
          {RESOURCES.filter(r => !r.isLocked).length} free
        </span>
      </div>

      {/* Featured Tool */}
      <div className="bg-neutral-900 rounded-3xl p-5 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-neutral-400 mb-1">Featured</p>
            <h3 className="text-lg font-semibold text-white mb-1">Profit Calculator</h3>
            <p className="text-sm text-neutral-400">See your potential earnings</p>
          </div>
          <button className="bg-white text-neutral-900 px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center transition-all hover:bg-neutral-100 active:scale-[0.98]">
            Try it
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {CATEGORIES.map(category => (
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              activeCategory === category.id
                ? 'bg-neutral-900 text-white'
                : 'bg-white text-neutral-600 border border-neutral-200'
            }`}
          >
            {category.label}
          </button>
        ))}
      </div>

      {/* Resources List */}
      <div className="space-y-2">
        {filteredResources.map(resource => {
          const isAccessible = !resource.isLocked || isAuthenticated;
          const IconComponent = TYPE_ICONS[resource.type];
          const isBookmarked = bookmarkedResources.includes(resource.id);

          return (
            <div
              key={resource.id}
              className={`w-full bg-white rounded-2xl p-4 border flex items-center transition-all ${
                isAccessible
                  ? 'border-neutral-200/60 hover:border-neutral-300'
                  : 'border-neutral-100 opacity-50'
              }`}
            >
              <button
                onClick={() => handleDownload(resource)}
                disabled={!isAccessible}
                className={`w-10 h-10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 ${
                  isAccessible ? 'bg-neutral-100 cursor-pointer' : 'bg-neutral-50 cursor-not-allowed'
                }`}
              >
                <IconComponent className={`w-5 h-5 ${isAccessible ? 'text-neutral-600' : 'text-neutral-300'}`} />
              </button>

              <button
                onClick={() => handleDownload(resource)}
                disabled={!isAccessible}
                className={`flex-1 min-w-0 text-left ${isAccessible ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              >
                <h3 className={`font-semibold text-sm ${isAccessible ? 'text-neutral-900' : 'text-neutral-400'}`}>
                  {resource.title}
                </h3>
                <p className="text-xs text-neutral-400 capitalize">{resource.type}</p>
              </button>

              {/* Bookmark Button */}
              {isAccessible && onToggleBookmark && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleBookmark(resource.id, resource.title);
                  }}
                  className="p-2 rounded-lg hover:bg-neutral-100 transition-colors mr-1"
                >
                  <Bookmark
                    className={`w-4 h-4 ${isBookmarked ? 'fill-neutral-900 text-neutral-900' : 'text-neutral-300'}`}
                  />
                </button>
              )}

              {isAccessible ? (
                <Download className="w-5 h-5 text-neutral-400 flex-shrink-0" />
              ) : (
                <Lock className="w-4 h-4 text-neutral-300 flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>

      {/* Unlock CTA */}
      {!isAuthenticated && (
        <button
          onClick={onLoginClick}
          className="w-full mt-6 p-4 rounded-2xl border-2 border-dashed border-neutral-200 text-center transition-all hover:border-neutral-300 hover:bg-neutral-100/50"
        >
          <p className="font-semibold text-neutral-900 text-sm">Unlock all tools</p>
          <p className="text-xs text-neutral-400 mt-0.5">Sign up free</p>
        </button>
      )}
    </div>
  );
};

export default ResourceLibrary;
